import {
  getContactInformationMachine,
  ParentState,
  getFlightPassengerInformationMachine,
  getCartQuoteMachine,
  getReviewMachineState,
  getSeatMachine,
  getCartUpdateMachine,
  FlightServiceTypes,
  CartQuoteGuardTypes,
  getPassportMachineState,
  getPriceFreezeMachineState,
  getPriceFreezeDiscountMachineState,
  getCancelForAnyReasonMachine,
  GenericChildState,
  combineServices,
  getAirDisruptionMachineV1,
  CartGuards,
  FlightActionTypes,
  getVipSupportMachine,
  VipSupportEventTypes,
  VipSupportActionTypes,
  getChfarMachine,
  getChfarExerciseDiscountMachine,
  FlightPassengerActionTypes,
  FlightPassengerEventTypes,
  type PartialParentContext,
  SeatActionTypes,
} from "@hopper-b2b/checkout";
import type { MachineConfig } from "xstate";

import { ActionTypes } from "./actions";
import { Event, type TEvent } from "./events";
import { GuardTypes } from "./guards";
import { getInitialFlightMachineContext } from "./initialContext";
import { ServiceTypes, Services } from "./services";
import { getAirPriceDropMachine } from "./states/AirPriceDrop";
import { getCartFulfillMachine } from "./states/CartFulfill";
import type { FlightMachineContext } from "./types";
import { getCheckoutStepOnQueryParam } from "./utilities";

export const flightMachineId = "uber-flight-checkout";
export const flightStateMachine: MachineConfig<
  FlightMachineContext,
  any,
  TEvent
> = {
  schema: {
    context: getInitialFlightMachineContext(),
    events: {} as TEvent,
  },

  // Machine identifier
  id: flightMachineId,

  // Initial state
  initial: ParentState.route,

  // State definitions

  states: {
    [ParentState.route]: {
      initial: "goToReview",
      states: {
        goToReview: {
          // Handle back from pricefreeze entry on review step
          always: [
            {
              cond: (ctx: PartialParentContext) =>
                getCheckoutStepOnQueryParam() === ParentState.review &&
                !!ctx.cartQuote.priceQuoteCipherText,
              target: `#${flightMachineId}.${ParentState.review}`,
            },
            { target: `#${flightMachineId}.${ParentState.priceFreeze}` },
          ],
        },
      },
    },
    [ParentState.priceFreeze]: {
      ...getPriceFreezeMachineState({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.loading}`,
      }),
    },
    [ParentState.loading]: {
      entry: ActionTypes.setInitialContactInformation,
      initial: "fetchTripPricing",
      states: {
        fetchTripPricing: {
          invoke: {
            src: FlightServiceTypes.fetchTripPricingService,
            onDone: [
              {
                actions: ActionTypes.setTripPricingFromData,
                target: `#${flightMachineId}.${ParentState.passengerInformation}`,
                cond: GuardTypes.contactContinue,
              },
              {
                actions: ActionTypes.setTripPricingFromData,
                target: `#${flightMachineId}.${ParentState.contactInformation}`,
              },
            ],
            onError: [
              {
                actions: ActionTypes.setTripPricingError,
                target: `#${flightMachineId}.${ParentState.passengerInformation}`,
                cond: GuardTypes.contactContinue,
              },
              {
                actions: ActionTypes.setTripPricingError,
                target: `#${flightMachineId}.${ParentState.contactInformation}`,
              },
            ],
          },
        },
      },
    },
    [ParentState.contactInformation]: {
      ...getContactInformationMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.passengerInformation}`,
        nextStateFromReview: `#${flightMachineId}.${ParentState.cartQuote}`,
      }),
    },
    [ParentState.passengerInformation]: {
      ...getFlightPassengerInformationMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.cartQuote}`,
        nextStateFromReview: `#${flightMachineId}.${ParentState.cartQuote}`,
        previousState: `#${flightMachineId}.${ParentState.contactInformation}`,
      }),
    },
    [ParentState.passport]: {
      ...getPassportMachineState({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.passengerInformation}`,
        previousState: `#${flightMachineId}.${ParentState.passengerInformation}`,
      }),
    },
    [ParentState.cartQuote]: {
      entry: [
        FlightActionTypes.addMultiProviderProduct,
        SeatActionTypes.resetSelectedSeats,
      ],
      ...getCartQuoteMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.cancelForAnyReason}`,
      }),
    },
    // Add Products to Cart
    [ParentState.cancelForAnyReason]: {
      ...getCancelForAnyReasonMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.changeForAnyReason}`,
      }),
    },
    [ParentState.changeForAnyReason]: {
      ...getChfarMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.disruption}`,
      }),
    },
    [ParentState.disruption]: {
      ...getAirDisruptionMachineV1({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.vipSupport}`,
      }),
    },
    [ParentState.vipSupport]: {
      ...getVipSupportMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.priceDrop}`,
      }),
    },
    [ParentState.priceDrop]: {
      ...getAirPriceDropMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.priceFreezeDiscount}`,
      }),
    },
    //Add Discounts to Cart
    [ParentState.priceFreezeDiscount]: {
      ...getPriceFreezeDiscountMachineState({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.changeForAnyReasonDiscount}`,
      }),
    },
    [ParentState.changeForAnyReasonDiscount]: {
      ...getChfarExerciseDiscountMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.cartUpdate}`,
      }),
    },
    // Update cart with Products and Discounts
    [ParentState.cartUpdate]: {
      ...getCartUpdateMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.review}`,
      }),
      exit: FlightActionTypes.checkForPriceChange,
    },
    [ParentState.seatSelection]: {
      ...getSeatMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.review}`,
        previousState: `#${flightMachineId}.${ParentState.review}`,
      }),
    },
    [ParentState.review]: {
      initial: GenericChildState.loading,
      ...getReviewMachineState({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.cartUpdateBeforeFulfill}`,
        previousState: `#${flightMachineId}.${ParentState.passengerInformation}`,
        stateOverride: {
          [GenericChildState.loading]: {
            invoke: {
              src: ServiceTypes.trackReviewDetails,
              onDone: `#${flightMachineId}.${ParentState.review}.${GenericChildState.idle}`,
              onError: `#${flightMachineId}.${ParentState.review}.${GenericChildState.idle}`,
            },
          },
          [GenericChildState.idle]: {},
        },
      }),
      exit: VipSupportActionTypes.removeVipProductFromCart,
    },
    [ParentState.cartUpdateBeforeFulfill]: {
      ...getCartUpdateMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.payment}`,
      }),
    },
    [ParentState.payment]: {
      initial: GenericChildState.loading,
      states: {
        [GenericChildState.loading]: {
          invoke: {
            src: (ctx, event) =>
              combineServices([
                Services.trackViewPayment(ctx, event),
                Services.trackUberPayment(ctx, event),
              ]),
            onDone: `#${flightMachineId}.${ParentState.payment}.${GenericChildState.idle}`,
            onError: `#${flightMachineId}.${ParentState.payment}.${GenericChildState.idle}`,
          },
        },
        [GenericChildState.idle]: {},
      },
      on: {
        [Event.SET_PAYMENT_DATA_AND_GO_NEXT]: {
          actions: ActionTypes.setPaymentData,
          target: `#${flightMachineId}.${ParentState.cartFulfill}`,
        },
        [Event.PREVIOUS]: [
          {
            target: `#${flightMachineId}.${ParentState.passengerInformation}`,
            cond: CartGuards.isCartLocked,
          },
          {
            target: `#${flightMachineId}.${ParentState.review}`,
          },
        ],
        [Event.NEXT]: {
          target: `#${flightMachineId}.${ParentState.cartFulfill}`,
        },
      },
    },
    [ParentState.cartFulfill]: {
      ...getCartFulfillMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.bookingConfirmation}`,
      }),
    },
    [ParentState.bookingConfirmation]: {},
  },
  on: {
    [Event.OPEN_PASSENGER_PICKER]: `#${flightMachineId}.${ParentState.passengerInformation}`,
    [VipSupportEventTypes.TOGGLE_SUPPORT_STATUS]: {
      actions: VipSupportActionTypes.toggleVipSupport,
    },
    // Events below are used for browser navigation
    [Event.GO_TO_PASSENGER_SELECT]: `#${flightMachineId}.${ParentState.passengerInformation}`,
    [Event.GO_TO_CONTACT_INFORMATION]: `#${flightMachineId}.${ParentState.contactInformation}`,
    [Event.GO_TO_CART_QUOTE]: `#${flightMachineId}.${ParentState.cartQuote}`,
    [Event.GO_TO_REVIEW]: `#${flightMachineId}.${ParentState.review}`,
    [Event.GO_TO_PAYMENT]: `#${flightMachineId}.${ParentState.payment}`,
    [Event.GO_TO_BOOKING_CONFIRMATION]: `#${flightMachineId}.${ParentState.bookingConfirmation}`,
    [Event.GO_TO_PRICE_DROP]: `#${flightMachineId}.${ParentState.priceDrop}`,
    [Event.GO_TO_SEAT_SELECTION]: [
      {
        target: `#${flightMachineId}.${ParentState.cartQuote}`,
        cond: CartQuoteGuardTypes.isCartLocked,
      },
      {
        target: `#${flightMachineId}.${ParentState.seatSelection}`,
      },
    ],
    [Event.EDIT_SEATS_FROM_REVIEW]: [
      {
        actions: ActionTypes.setEditSeatsFromReview,
        target: `#${flightMachineId}.${ParentState.seatSelection}`,
      },
    ],
    [FlightPassengerEventTypes.OPEN_FORM_AND_SET_PASSENGER]: {
      target: `#${flightMachineId}.${ParentState.passengerInformation}`,
      actions: FlightPassengerActionTypes.openFormAndSetCurrentPassenger,
    },
  },
};
